import { PdfGeometryImportResponse } from '@shared/models/design/pdf-geometry-import.model';
import { createBuildingDesign } from './factories/design.factory';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import { Storey } from '@shared/models/design/storey.model';
import { createStoreys } from './factories/storey.factory';
import { createShape } from './factories/shape.factory';
import {
  getCenter,
  isEqualPoints,
  isPolygon2D,
  movePoints,
  toPoint2D,
} from '../geometry/geometry.helpers';
import {
  normalizeDesignLocationAndPoints,
  normalizePoints,
} from './design.helpers';
import {
  getAllStoreys,
  getBuildingById,
  getBuildingStoreys,
  getEmptyBuildingPlacementOrigin,
  setBuildingStoreysWithProgramSeed,
} from './storey.helpers';
import type { Point2D, Polygon2D } from '@shared/models/geometry.model';

/**
 * Floor-to-floor heights for {@link createStoreys}. Prefers an explicit stack;
 * otherwise splits total height into `storeyCount` equal floors.
 */
const getStoreyHeights = ({
  storeyHeightsMeters,
  storeyCount,
  buildingHeightMeters,
}: Pick<
  PdfGeometryImportResponse,
  'storeyHeightsMeters' | 'storeyCount' | 'buildingHeightMeters'
>): number[] => {
  if (storeyHeightsMeters?.length) {
    return storeyHeightsMeters;
  }
  if (storeyCount) {
    const floorToFloor = buildingHeightMeters / storeyCount;
    return Array.from({ length: storeyCount }, () => floorToFloor);
  }
  return [buildingHeightMeters];
};

/**
 * Move an origin-centered polygon onto the target building. Keeps the previous
 * footprint center when that building already has geometry; otherwise offsets
 * beside siblings like empty-GFA placement.
 */
const placeImportedFootprintPoints = (
  design: BuildingDesign,
  buildingId: string | undefined,
  originCenteredPoints: Polygon2D,
): Point2D[] => {
  const existingPoints = getBuildingStoreys(design, buildingId).flatMap(
    (storey) => storey.shapes.flatMap((shape) => shape.points),
  );
  if (existingPoints.length) {
    return movePoints(originCenteredPoints, getCenter(...existingPoints));
  }
  const xs = originCenteredPoints.map(([x]) => x);
  const width = Math.max(...xs) - Math.min(...xs);
  return movePoints(
    originCenteredPoints,
    getEmptyBuildingPlacementOrigin(design, buildingId, width),
  );
};

/**
 * Replace storeys of {@link buildingId} (or the first building) with geometry
 * inferred from a geometry import. Programs are copied from the nearest
 * existing storey on that building.
 * Throws when {@link buildingId} is set but no building matches.
 */
export const importDesignFromPdf = (
  importResponse: PdfGeometryImportResponse,
  existingDesign: BuildingDesign = createBuildingDesign(),
  buildingId?: string,
): BuildingDesign => {
  if (buildingId && !getBuildingById(existingDesign, buildingId)) {
    throw new Error(`No building with id ${buildingId}`);
  }
  existingDesign = normalizeDesignLocationAndPoints(existingDesign); // Make sure location and points are in sync
  const storeyHeights = getStoreyHeights(importResponse);
  const normalizedPoints = normalizePoints(
    importResponse.points2D.map(toPoint2D),
  );
  if (!isPolygon2D(normalizedPoints)) {
    throw new Error('Points must be a valid polygon');
  }
  const points = placeImportedFootprintPoints(
    existingDesign,
    buildingId,
    normalizedPoints,
  );
  if (!isPolygon2D(points)) {
    throw new Error('Points must be a valid polygon');
  }
  const existingStoreys = getBuildingStoreys(existingDesign, buildingId);

  const getNearestStoreyActivityRefs = (index: number) => {
    const nearestStorey =
      existingStoreys[Math.min(index, existingStoreys.length - 1)];
    if (!nearestStorey) {
      return [];
    }
    return nearestStorey.activities.map((activity) => ({
      ...activity,
    }));
  };

  const nextStoreys = createStoreys(
    storeyHeights.map((height, index) => ({
      height,
      shapes: [createShape({ points })],
      activities: getNearestStoreyActivityRefs(index),
    })),
  );

  return normalizeDesignLocationAndPoints(
    setBuildingStoreysWithProgramSeed(existingDesign, buildingId, nextStoreys),
  );
};

/**
 * True when {@link design} already has the footprint and storey stack from a
 * geometry import. Ignores storey ids so a successful persist still matches
 * after {@link createStoreys} regenerates them.
 */
export const designMatchesGeometryImport = (
  design: BuildingDesign,
  importResponse: PdfGeometryImportResponse,
): boolean => {
  const expected = importDesignFromPdf(importResponse, design);
  return areImportedStoreyStacksEqual(
    getAllStoreys(expected),
    getAllStoreys(design),
  );
};

/** Compare imported floor heights and footprints, ignoring regenerated storey ids. */
const areImportedStoreyStacksEqual = (
  expected: readonly Storey[],
  actual: readonly Storey[],
): boolean => {
  if (expected.length !== actual.length) return false;
  return expected.every((storey, index) => {
    const other = actual[index];
    if (!other || storey.height !== other.height) return false;
    if (storey.shapes.length !== other.shapes.length) return false;
    return storey.shapes.every((shape, shapeIndex) => {
      const otherShape = other.shapes[shapeIndex];
      if (!otherShape || shape.points.length !== otherShape.points.length) {
        return false;
      }
      return shape.points.every((point, pointIndex) => {
        const otherPoint = otherShape.points[pointIndex];
        if (!otherPoint) return false;
        return isEqualPoints(point, otherPoint);
      });
    });
  });
};
